export const PINNED_MODULE_NAMES = [
	'Combustible',
	'EPP',
	'Residuos',
	'Normativa',
];

function asId(value) {
	if (value == null || value === '') {
		return undefined;
	}
	if (typeof value === 'object' && value.$oid) {
		return String(value.$oid);
	}
	return String(value);
}

function isDisabled(establishment) {
	return (
		Boolean(establishment) &&
		(establishment.disable === true || establishment.disabled === true)
	);
}

function indexById(items) {
	const index = {};
	asList(items).forEach(item => {
		const id = asId(item && item._id);
		if (id) {
			index[id] = item;
		}
	});
	return index;
}

function asList(value) {
	return Array.isArray(value) ? value : [];
}

function resolveCompanyId(establishment) {
	if (!establishment) {
		return undefined;
	}
	return establishment.company_id || establishment.company?._id;
}

function resolveStatusId(establishment) {
	if (!establishment) {
		return undefined;
	}
	return (
		establishment.establishment_status_id ||
		establishment.establishment_status?._id
	);
}

function buildPinnedColumns(modules) {
	const byName = {};
	asList(modules).forEach(module => {
		const moduleId = asId(module && module._id);
		if (moduleId && module.name && !byName[module.name]) {
			byName[module.name] = { ...module, _id: moduleId };
		}
	});
	return PINNED_MODULE_NAMES.filter(name => byName[name]).map(name => ({
		moduleId: byName[name]._id,
		name,
	}));
}

function indexFactsByEstablishment(facts) {
	const byEstablishment = new Map();
	asList(facts).forEach(fact => {
		const establishmentId = asId(fact && fact.establishment_id);
		const moduleId = asId(fact && fact.module_id);
		if (!establishmentId || !moduleId) {
			return;
		}
		if (typeof fact.used !== 'boolean') {
			return;
		}
		if (!byEstablishment.has(establishmentId)) {
			byEstablishment.set(establishmentId, {});
		}
		byEstablishment.get(establishmentId)[moduleId] = fact.used;
	});
	return byEstablishment;
}

export function deriveBaseCell(usedByModuleId = {}, columns = []) {
	if (columns.length < PINNED_MODULE_NAMES.length) {
		return undefined;
	}
	const values = columns.map(column => usedByModuleId[column.moduleId]);
	if (values.some(value => value !== true && value !== false)) {
		return undefined;
	}
	if (values.some(value => value === true)) {
		return true;
	}
	return false;
}

export function buildActCensus(storedFacts = [], catalogs = {}) {
	const facts = asList(storedFacts);
	const establishments = asList(catalogs.establishments);
	const statusesById = indexById(catalogs.statuses);
	const pinnedIds = new Set(
		buildPinnedColumns(catalogs.modules).map(column => asId(column.moduleId)),
	);

	const actEstablishments = establishments.filter(establishment => {
		if (!establishment || !asId(establishment._id) || isDisabled(establishment)) {
			return false;
		}
		const status = statusesById[asId(resolveStatusId(establishment))];
		return status?.code === 'ACT';
	});

	const actIds = new Set(
		actEstablishments.map(establishment => asId(establishment._id)),
	);
	const actCount = actIds.size;
	const expected = actCount * 4;

	const storedKeys = new Set();
	facts.forEach(fact => {
		const establishmentId = asId(fact && fact.establishment_id);
		const moduleId = asId(fact && fact.module_id);
		if (!establishmentId || !actIds.has(establishmentId)) {
			return;
		}
		if (!pinnedIds.has(moduleId)) {
			return;
		}
		if (typeof fact.used !== 'boolean') {
			return;
		}
		storedKeys.add(`${establishmentId}:${moduleId}`);
	});

	const stored = storedKeys.size;
	return {
		expected,
		stored,
		complete: stored === expected,
		actCount,
	};
}

export function filterRowsByStatus(rows = [], { includeNop = false } = {}) {
	return asList(rows).filter(row => {
		if (!row) {
			return false;
		}
		if (row.statusCode === 'NOP') {
			return includeNop;
		}
		return true;
	});
}

export function groupRowsByCompany(rows = []) {
	const groups = [];
	const byCompany = new Map();
	asList(rows).forEach(row => {
		if (!row) {
			return;
		}
		const key = row.companyId || '';
		if (!byCompany.has(key)) {
			const group = {
				companyId: row.companyId,
				companyName: row.companyName,
				rows: [],
			};
			byCompany.set(key, group);
			groups.push(group);
		}
		byCompany.get(key).rows.push(row);
	});
	return groups;
}

export function mapStoredFacts(storedFacts = [], catalogs = {}) {
	const facts = asList(storedFacts);
	const establishments = asList(catalogs.establishments);
	const statusesById = indexById(catalogs.statuses);
	const companiesById = indexById(catalogs.companies);
	const columns = buildPinnedColumns(catalogs.modules);
	const factsByEstablishment = indexFactsByEstablishment(facts);

	const rows = [];
	const seenIds = new Set();
	establishments.forEach(establishment => {
		const establishmentId = asId(establishment && establishment._id);
		if (!establishmentId || seenIds.has(establishmentId)) {
			return;
		}
		seenIds.add(establishmentId);
		const companyId = asId(resolveCompanyId(establishment));
		const status = statusesById[asId(resolveStatusId(establishment))];
		const company = companiesById[companyId];
		const usedByModuleId =
			factsByEstablishment.get(establishmentId) || {};
		rows.push({
			establishmentId,
			name: establishment.name || establishmentId,
			companyId,
			companyName: company?.name,
			statusCode: status?.code,
			statusName: status?.name,
			disabled: isDisabled(establishment),
			usedByModuleId,
			base: deriveBaseCell(usedByModuleId, columns),
		});
	});

	return {
		columns,
		rows,
		census: buildActCensus(facts, catalogs),
	};
}
